import {
  Flex,
  IconButton,
  Input,
  NativeSelect,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { Check, Copy, Dices } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ConfirmDialog, copyToClipboard, Field } from '@mangovoice/mango-ui'

import { useDevicesI18n } from '../i18n'
import type {
  DeviceModelItem,
  ProvisioningUserItem,
} from '../types/device.types'
import { normalizeMac } from '../utils/mac'

const COMMON_YEALINK_MODELS = [
  'T73W',
  'T85W',
  'T87W',
  'AX83H',
  'T34W',
  'W70B',
  'T54W',
  'T57W',
  'T53',
] as const

const normalizeModelName = (name: string) =>
  name.replace(/\s+/g, '').toUpperCase()

export interface ExtensionOption {
  id: number
  number: string
  name?: string
}

interface ModelSelectProps {
  value: number | ''
  onChange: (value: number | '') => void
  models: DeviceModelItem[]
  disabled?: boolean
  error?: string
  required?: boolean
}

export const ModelSelect = React.memo<ModelSelectProps>(
  ({ value, onChange, models, disabled, error, required }) => {
    const { text } = useDevicesI18n()
    const mostCommonModels = useMemo(() => {
      const byNormalizedName = new Map(
        models.map((m) => [normalizeModelName(m.name), m] as const),
      )
      return COMMON_YEALINK_MODELS.map((name) =>
        byNormalizedName.get(name),
      ).filter((m): m is DeviceModelItem => Boolean(m))
    }, [models])

    const grouped = useMemo(() => {
      const map = new Map<string, DeviceModelItem[]>()
      for (const m of models) {
        const group = map.get(m.manufacturer_name) ?? []
        group.push(m)
        map.set(m.manufacturer_name, group)
      }
      return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
    }, [models])

    return (
      <Field.Root required={required} invalid={Boolean(error)}>
        <Field.Label>{text('devices.form.model', 'Model')}</Field.Label>
        <NativeSelect.Root
          size="sm"
          disabled={disabled}
          invalid={Boolean(error)}
        >
          <NativeSelect.Field
            value={value}
            onChange={(e) => {
              const v = e.target.value
              onChange(v === '' ? '' : Number(v))
            }}
          >
            {value === '' && (
              <option value="">
                {text('devices.form.selectModel', 'Select device model…')}
              </option>
            )}
            {mostCommonModels.length > 0 && (
              <optgroup
                label={text(
                  'devices.form.mostCommonYealink',
                  'Most Common (Yealink)',
                )}
              >
                {mostCommonModels.map((m) => (
                  <option key={`common-${m.id}`} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </optgroup>
            )}
            {grouped.map(([manufacturer, items]) => (
              <optgroup key={manufacturer} label={manufacturer}>
                {items.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    )
  },
)
ModelSelect.displayName = 'ModelSelect'

interface UserSelectProps {
  value: number | ''
  onChange: (value: number | '') => void
  users: ProvisioningUserItem[]
  disabled?: boolean
  error?: string
  required?: boolean
}

export const UserSelect = React.memo<UserSelectProps>(
  ({ value, onChange, users, disabled, error, required }) => {
    const { text } = useDevicesI18n()
    const userLabel = (u: ProvisioningUserItem) =>
      u.username ? `${u.full_name} (${u.username})` : u.full_name

    return (
      <Field.Root required={required} invalid={Boolean(error)}>
        <Field.Label>
          {text('devices.form.assignedUser', 'Assigned User')}
        </Field.Label>
        <NativeSelect.Root
          size="sm"
          disabled={disabled}
          invalid={Boolean(error)}
        >
          <NativeSelect.Field
            value={value === '' ? '' : value}
            onChange={(e) =>
              onChange(e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            <option value="">{text('devices.common.none', '— None —')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {userLabel(u)}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    )
  },
)
UserSelect.displayName = 'UserSelect'

interface ExtensionSelectProps {
  value: number | ''
  onChange: (value: number | '') => void
  extensions: ExtensionOption[]
  disabled?: boolean
  label?: string
  helperText?: string
  error?: string
  required?: boolean
}

export const ExtensionSelect = React.memo<ExtensionSelectProps>(
  ({
    value,
    onChange,
    extensions,
    disabled,
    label: labelProp,
    helperText,
    error,
    required,
  }) => {
    const { text } = useDevicesI18n()
    const label =
      labelProp ??
      text('devices.form.primaryLineExtension', 'Primary Line Extension')
    const sorted = useMemo(
      () =>
        [...extensions].sort(
          (a, b) => parseInt(a.number, 10) - parseInt(b.number, 10),
        ),
      [extensions],
    )

    return (
      <Field.Root required={required} invalid={Boolean(error)}>
        <Field.Label>{label}</Field.Label>
        <NativeSelect.Root
          size="sm"
          disabled={disabled}
          invalid={Boolean(error)}
        >
          <NativeSelect.Field
            value={value === '' ? '' : value}
            onChange={(e) =>
              onChange(e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            <option value="">{text('devices.common.none', '— None —')}</option>
            {sorted.map((ext) => (
              <option key={ext.id} value={ext.id}>
                {ext.number} {ext.name ? `— ${ext.name}` : ''}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {error ? (
          <Field.ErrorText>{error}</Field.ErrorText>
        ) : helperText ? (
          <Text fontSize="xs" color="fg.muted" mt={1}>
            {helperText}
          </Text>
        ) : null}
      </Field.Root>
    )
  },
)
ExtensionSelect.displayName = 'ExtensionSelect'

function generateRandomMac(): string {
  const bytes = Array.from({ length: 6 }, () => Math.floor(Math.random() * 256))
  bytes[0] = (bytes[0] | 0x02) & 0xfe
  return bytes
    .map((b) => b.toString(16).toUpperCase().padStart(2, '0'))
    .join(':')
}

interface MacAddressInputProps {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  onEnterKey?: () => void
  error: string | null
  validating: boolean
  disabled?: boolean
  required?: boolean
  /**
   * When true, the dice button prompts for confirmation before generating a
   * new MAC. Use on the edit page so a misclick can't silently rebind a
   * provisioned device.
   */
  confirmOnGenerate?: boolean
  /**
   * When true, render a button to copy the MAC in the lowercase, no-colons
   * format used by manual provisioning URLs. Only useful on the edit page.
   */
  showProvisioningCopy?: boolean
}

export const MacAddressInput = React.memo<MacAddressInputProps>(
  ({
    value,
    onChange,
    onBlur,
    onEnterKey,
    error,
    validating,
    disabled,
    required,
    confirmOnGenerate = false,
    showProvisioningCopy = false,
  }) => {
    const { text } = useDevicesI18n()
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
      'idle',
    )
    const performGenerate = useCallback(() => {
      onChange(generateRandomMac())
    }, [onChange])
    const handleGenerate = useCallback(() => {
      if (confirmOnGenerate) {
        setConfirmOpen(true)
        return
      }
      performGenerate()
    }, [confirmOnGenerate, performGenerate])

    const provisioningMac = normalizeMac(value).toLowerCase()
    const canCopyProvisioning =
      showProvisioningCopy && provisioningMac.length === 12

    const copiedTimerRef = useRef<number | null>(null)
    useEffect(
      () => () => {
        if (copiedTimerRef.current !== null) {
          window.clearTimeout(copiedTimerRef.current)
        }
      },
      [],
    )

    const handleCopyProvisioning = useCallback(async () => {
      if (!canCopyProvisioning) return
      let nextState: 'copied' | 'failed' = 'copied'
      try {
        // Uses copyToClipboard's textarea fallback so this works inside the
        // Admin2 iframe, which doesn't grant `clipboard-write` permission.
        await copyToClipboard(provisioningMac)
      } catch {
        nextState = 'failed'
      }
      setCopyState(nextState)
      if (copiedTimerRef.current !== null) {
        window.clearTimeout(copiedTimerRef.current)
      }
      copiedTimerRef.current = window.setTimeout(() => {
        setCopyState('idle')
        copiedTimerRef.current = null
      }, 1500)
    }, [canCopyProvisioning, provisioningMac])

    return (
      <Field.Root invalid={!!error} required={required}>
        <Field.Label>
          {text('devices.form.macAddress', 'MAC Address')}
        </Field.Label>
        <Flex gap={1} align="center">
          <Input
            size="sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              onEnterKey?.()
            }}
            placeholder={text(
              'devices.form.macPlaceholder',
              'AA:BB:CC:DD:EE:FF',
            )}
            fontFamily="mono"
            disabled={disabled}
            flex="1"
          />
          {showProvisioningCopy && (
            <Tooltip.Root openDelay={300}>
              <Tooltip.Trigger asChild>
                <IconButton
                  aria-label={text(
                    'devices.form.copyProvisioningMac',
                    'Copy MAC for provisioning URL',
                  )}
                  size="sm"
                  variant="outline"
                  onClick={handleCopyProvisioning}
                  disabled={disabled || !canCopyProvisioning}
                >
                  {copyState === 'copied' ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </IconButton>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>
                  {copyState === 'copied'
                    ? text(
                        'devices.form.copyProvisioningMacCopied',
                        'Copied {{value}}',
                        { value: provisioningMac },
                      )
                    : copyState === 'failed'
                      ? text(
                          'devices.form.copyProvisioningMacFailed',
                          'Copy failed. Select the value manually instead.',
                        )
                      : text(
                          'devices.form.copyProvisioningMacHint',
                          'Copy as lowercase, no colons (for provisioning URL)',
                        )}
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          )}
          <Tooltip.Root openDelay={300}>
            <Tooltip.Trigger asChild>
              <IconButton
                aria-label={text(
                  'devices.form.generateRandomMac',
                  'Generate random MAC address',
                )}
                size="sm"
                variant="outline"
                onClick={handleGenerate}
                disabled={disabled}
              >
                <Dices size={16} />
              </IconButton>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>
                {text(
                  'devices.form.generateRandomMac',
                  'Generate random MAC address',
                )}
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        </Flex>
        {validating && (
          <Field.HelperText>
            {text('devices.form.checkingUniqueness', 'Checking uniqueness…')}
          </Field.HelperText>
        )}
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
        {confirmOnGenerate && (
          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            title={text(
              'devices.form.generateRandomMacConfirmTitle',
              'Replace MAC address?',
            )}
            description={text(
              'devices.form.generateRandomMacConfirmDescription',
              'Generating a random MAC address will replace the current value. Are you sure you want to continue?',
            )}
            confirmLabel={text(
              'devices.form.generateRandomMacConfirmAction',
              'Generate new MAC',
            )}
            cancelLabel={text('devices.common.cancel', 'Cancel')}
            variant="destructive"
            onConfirm={() => {
              setConfirmOpen(false)
              performGenerate()
            }}
          />
        )}
      </Field.Root>
    )
  },
)
MacAddressInput.displayName = 'MacAddressInput'

interface StationLabelInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
}

export const StationLabelInput = React.memo<StationLabelInputProps>(
  ({
    value,
    onChange,
    placeholder: placeholderProp,
    error,
    required,
    disabled,
  }) => {
    const { text } = useDevicesI18n()
    const placeholder =
      placeholderProp ??
      text('devices.form.stationNamePlaceholder', 'e.g. Front desk')
    return (
      <Field.Root required={required} invalid={Boolean(error)}>
        <Field.Label>
          {text('devices.form.stationName', 'Station Name')}
        </Field.Label>
        <Input
          size="sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    )
  },
)
StationLabelInput.displayName = 'StationLabelInput'
